import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { imageUrl } from '../lib/images';

export default function OmLundeMediaPage() {
  return (
    <>
      <SEO
        title="Om Lunde Media"
        description="Mød Michael fra Lunde Media. Professionel fotograf og videograf med base i Kolding. Billeder og video med nærvær, stemning, følelser og autentisk storytelling."
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] sm:min-h-[70vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/487316894-1134600408683405-7489097942827672171-n.jpg', 'width=1920,quality=85,format=auto')}
            alt="Lunde Media - fotografi og videografi"
            className="w-full h-full object-cover filter brightness-[0.55] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-stone-900/35" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-sans font-medium text-white tracking-tight leading-tight max-w-4xl mx-auto">
            Om Lunde Media
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-zinc-200 font-sans max-w-2xl mx-auto leading-relaxed">
            Fotograf & videograf med passion for autentiske øjeblikke, menneskelige relationer og levende fortællinger.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Tag en uforpligtende snak
            </Link>
          </div>
        </div>
      </section>

      {/* Editorial Story Section - Light White */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Portrait of Michael */}
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden border border-zinc-200 shadow-sm">
                <img
                  src={imageUrl('/_assets/487214857-1134600378683408-2220849442786892437-n.jpg', 'width=1000,quality=85,format=auto')}
                  alt="Michael - Fotograf og Videograf bag Lunde Media"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Editorial Presentation */}
            <div className="lg:col-span-7 space-y-6 font-sans">
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#141416]">
                Mød Michael – øjet bag Lunde Media
              </h2>

              <p className="text-lg text-zinc-800 leading-relaxed">
                Når du booker Lunde Media som fotograf eller videograf, får du ikke bare en, der trykker på en knap. Du får en sparringspartner, der går tæt på og fanger de øjeblikke, mennesker og detaljer, der fortæller historien om dig, din virksomhed eller dit arrangement.
              </p>

              <p className="text-base sm:text-lg text-zinc-600 leading-relaxed">
                Jeg er baseret i Kolding og arbejder i hele Danmark. Min tilgang bygger på, at de stærkeste billeder og videoer sjældent opstår ud fra stive positurer eller overfladiske opsætninger. De opstår, når der er ro, tillid og plads til at være sig selv.
              </p>

              <p className="text-base sm:text-lg text-zinc-600 leading-relaxed">
                Hver opgave tilpasses præcis det, du har brug for – uanset om det gælder de uerstattelige øjeblikke til et bryllup, den rå fart og intensitet ved et cykelløb, dynamisk dækning af en erhvervsevent eller de varme relationer i en børnefamilie.
              </p>

              <div className="pt-4 border-t border-zinc-200 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="font-semibold text-black mb-1">Base & Geografi</div>
                  <div className="text-zinc-600">Holder til i Kolding – dækker opgaver i hele landet.</div>
                </div>
                <div>
                  <div className="font-semibold text-black mb-1">Fokusområder</div>
                  <div className="text-zinc-600">Bryllupper, Cykelløb, Events & Erhverv, Familiefotografering.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section - Neutral Light Gray */}
      <section className="py-20 md:py-28 bg-[#F3F4F6] text-[#141416]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-4">
              Værdierne bag hvert eneste billede
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed">
              Teknisk udstyr er blot et værktøj. Det, der gør forskellen, er evnen til at se stemninger, forstå mennesker og fortælle en historie, som vækker følelser mange år efter.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Storytelling</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Hver fotoserie eller videoproduktion er bygget som en sammenhængende fortælling med en rød tråd fra start til slut.
              </p>
            </div>

            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Nærvær & Ro</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                En afslappet, behagelig og nærværende stemning skaber tillid og gør, at I glemmer kameraet og bare er til stede.
              </p>
            </div>

            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Menneskelige Relationer</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Blikke, berøringer, latter og fællesskab. Det er dynamikken mellem mennesker, der gør billeder mindeværdige.
              </p>
            </div>

            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Autenticitet</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Ingen stive eller kunstige smil. Jeg fanger øjeblikkene, som de rent faktisk udfolder sig i det virkelige liv.
              </p>
            </div>

            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Både Foto & Video</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Stærke stilbilleder kombineret med levende lyd og bevægelse giver en komplet dokumentation af dagen eller arrangementet.
              </p>
            </div>

            <div className="p-8 bg-white border border-zinc-200">
              <h3 className="text-xl font-medium mb-2 text-black">Professionel Levering</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Hurtig, tryg proces fra den første dialog til overlevering af færdigredigerede billeder og video i højeste opløsning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote / Philosophy Banner */}
      <section className="py-20 md:py-24 bg-white text-[#141416] border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <blockquote className="text-2xl sm:text-3xl md:text-4xl font-medium text-black leading-snug">
              "Lunde Media – jeg fanger øjeblikket, fortæller historien og giver dig billeder, du har lyst til at vise frem."
            </blockquote>
            <p className="text-base text-zinc-600">
              — Michael, Lunde Media
            </p>
          </div>
        </div>
      </section>

      {/* Bottom Call to Action */}
      <section className="py-20 md:py-28 bg-[#141416] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
            Lad os fortælle din historie
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed">
            Uanset om du planlægger et bryllup, en stor event eller ønsker billeder af familien eller virksomheden, er du altid velkommen til at kontakte mig.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-black font-semibold text-sm tracking-wide transition-all shadow-md"
            >
              Kontakt Michael
            </Link>
            <a
              href="tel:26368306"
              className="cta-btn inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span>26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
